import { format } from "date-fns"
import { selectedDonorAtom } from "hook/state/generic"
import React, { useEffect, useState } from "react"
import { useQuery } from "react-query"
import { useRecoilState } from "recoil"
import { formatMoney, occurrencesOf, toStartCase } from "utils/functions"
import makeData from "utils/makeData"
import useReports from "./useReports"
import zipcelx from "zipcelx"

const useDonorReport = () => {
  const [selectedDonor, setSelectedDonor] = useRecoilState(selectedDonorAtom);
  const [selectedDonorDetails, setSelectedDonorDetails] = useState(null);
  const { getDonorReport } = useReports()
  const options = []

  const { data, isLoading } = useQuery(['donors', 'report', selectedDonor?.value], () => getDonorReport(selectedDonor?.value), {
    select: data => data.data.donation_report,
    enabled: Boolean(selectedDonor?.value),
  })
  const [, setData] = React.useState(React.useMemo(() => makeData('aausers', 100), []))
  const [currentData, setCurrentData] = React.useState(null)
  const [current, setCurrent] = React.useState('')
  const [open, setOpen] = React.useState(false)
  const [edit, setEdit] = React.useState(false)
  const [bar_data, setBar_data] = React.useState([])
  const [line_data, setLine_data] = React.useState([])
  const [skipPageReset, setSkipPageReset] = React.useState(false)

  // Client-side date filter state
  const [dateFrom, setDateFrom] = React.useState('');
  const [dateTo, setDateTo] = React.useState('');

  // Filter data client-side by date range
  const filteredData = React.useMemo(() => {
    if (!data || !Array.isArray(data)) return [];
    if (!dateFrom && !dateTo) return data;
    return data.filter(row => {
      if (!row?.created_at) return true;
      const rowDate = new Date(row.created_at);
      if (dateFrom && rowDate < new Date(dateFrom)) return false;
      if (dateTo && rowDate > new Date(dateTo + 'T23:59:59')) return false;
      return true;
    });
  }, [data, dateFrom, dateTo]);

  // totalAmount based on filtered data
  const totalAmount = React.useMemo(() => {
    return filteredData.reduce((sum, row) => sum + parseFloat(row?.transaction?.amount || row?.amount || 0), 0);
  }, [filteredData]);

  const updateMyData = () => {
    setSkipPageReset(true)
    setData(old =>
      old.map((row) => {
        if (row.id === currentData.id) {
          return { ...currentData }
        }
        return row
      })
    )
    setOpen(false)
  }

  const dummy_data = {
    labels: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", 'Aug', "Sep", "Oct", "Nov", "Dec"],
    datasets: [{ label: "defaulters", backgroundColor: "rgba(2,117,216,1)", borderColor: "rgba(2,117,216,1)", data: bar_data }],
  };

  const dummy_line_data = {
    labels: line_data.length ? line_data.map(item => format(new Date(item.date), 'MMM dd')) : [],
    datasets: [{
      fill: true, label: "Total defaulters", lineTension: 0.3,
      backgroundColor: "rgba(2,117,216,0.2)", borderColor: "rgba(2,117,216,1)",
      pointRadius: 5, pointBackgroundColor: "rgba(2,117,216,1)", pointBorderColor: "rgba(255,255,255,0.8)",
      pointHoverRadius: 5, pointHoverBackgroundColor: "rgba(2,117,216,1)", pointHitRadius: 50, pointBorderWidth: 2,
      data: line_data.length ? line_data.map(item => item.count) : [],
    }],
  };

  useEffect(() => {
    const donationReportByMonth = async () => {
      const today = format(new Date(), 'yyyy/MM/dd')
      const date = today.split('/')[0]
      const records = []
      data.forEach(item => {
        if (item?.created_at?.includes(date)) {
          records.push(item.created_at.split('/')[1])
        }
      })
      const new_data = []
      Array.from(Array(12).keys(), item => {
        new_data.push(occurrencesOf(item + 1, records))
      })
      setBar_data(new_data)
    }

    const donationsThisMonthFunc = () => {
      const records = []
      data.forEach(item => {
        records.push({ date: item.created_at, count: 0 })
      })
      const reduced = records.sort((p1, p2) =>
        (p1.date > p2.date) ? 1 : (p1.date < p2.date) ? -1 : 0).reduce(function (allDates, date) {
          if (allDates.some(e => e.date === date.date)) {
            allDates.filter(e => e.date === date.date)[0].amount += +date.amount
          } else {
            allDates.push({ date: date.date, amount: +date.amount })
          }
          return allDates
        }, []);
      setLine_data(reduced)
    }

    if (data && data.length) {
      setSelectedDonorDetails(data[0])
      donationReportByMonth()
      donationsThisMonthFunc()
    } else {
      setSelectedDonorDetails(null)
    }
  }, [data, selectedDonor])

  const handleclick = (obj) => {
    setOpen(true)
    setCurrentData(obj)
  }

  const columns = React.useMemo(
    () => [
      {
        Header: 'S/N',
        id: 'serial',
        Cell: ({ row, state }) => {
          const { pageIndex = 0, pageSize = 10 } = state || {};
          return (pageIndex * pageSize) + row.index + 1;
        }
      },
     {
    Header: 'Name',
    id: 'donor_name',
    accessor: row => (
        [
            row?.user?.first_name,
            row?.user?.last_name
        ]
        .filter(Boolean)
        .map(name => toStartCase(name))
        .join(' ')
    ) || '-',
},
      {
        Header: 'Phone Number',
        id: 'donor_phone_number',
        accessor: row => row?.user?.phone_home || '-',
      },
      {
        Header: 'Amount',
        id: 'amount',
        accessor: row => formatMoney(row?.transaction?.amount || row?.amount),
      },
      {
        Header: 'Donation Date',
        id: 'date',
        accessor: row => row?.created_at ? format(new Date(row.created_at), 'MMM dd yyyy') : '-',
      },
      {
        Header: 'Cycle',
        id: 'donation_cycle',
        accessor: row => toStartCase(row?.donation_cycle) || '-',
      },
      {
        Header: 'Payment Purpose',
        id: 'payment_purpose',
        accessor: row => row?.payment_purpose || '-',
      },
      // {
      //   Header: 'Type',
      //   id: 'type',
      //   accessor: row => row?.payment_purpose === 'donation'
      //     ? (row?.donation_course?.tagline || row?.transactionable?.tagline || '-')
      //     : '-',
      // },
      {
        Header: 'Type',
        id: 'type',
        accessor: row => row?.donation_type || row?.transactionable?.tagline || '-'
      },
      {
        Header: 'Fundraise',
        id: 'fundraise',
        accessor: row => row?.payment_purpose === 'fund_raising'
          ? (row?.fund_raising_course?.name || row?.transactionable?.name || '-')
          : '-',
      },
    ],
    []
  )

  function getExcel() {
    if (!filteredData || !filteredData.length) return;

    const headers = [
      { value: 'S/N', type: 'string' },
      { value: 'Name', type: 'string' },
      { value: 'Phone Number', type: 'string' },
      { value: 'Amount', type: 'number' },
      { value: 'Donation Date', type: 'string' },
      { value: 'Cycle', type: 'string' },
      { value: 'Payment Purpose', type: 'string' },
      { value: 'Type', type: 'string' },
      { value: 'Fundraise', type: 'string' },
    ];

    const dataRows = filteredData.map((row, index) => [
      { value: index + 1, type: 'number' },
      {
        value: `${row?.user?.first_name || ''} ${row?.user?.last_name || ''}`.trim() || '-',
        type: 'string'
      },
      { value: row?.user?.phone_home || '-', type: 'string' },
      { value: parseFloat(row?.transaction?.amount || row?.amount || 0), type: 'number' },
      { value: row?.created_at ? format(new Date(row.created_at), 'MMM dd yyyy') : '-', type: 'string' },
      { value: row?.donation_cycle || '-', type: 'string' },
      { value: row?.payment_purpose || '-', type: 'string' },
      { value: row?.payment_purpose === 'donation' ? (row?.donation_course?.tagline || row?.transactionable?.tagline || '-') : '-', type: 'string' },
      { value: row?.payment_purpose === 'fund_raising' ? (row?.fund_raising_course?.name || row?.transactionable?.name || '-') : '-', type: 'string' },
    ]);

    const summationRow = [
      { value: '', type: 'string' },
      { value: '', type: 'string' },
      { value: 'Total Amount', type: 'string' },
      { value: totalAmount, type: 'number' },
      { value: '', type: 'string' },
      { value: '', type: 'string' },
      { value: '', type: 'string' },
      { value: '', type: 'string' },
      { value: '', type: 'string' },
    ];

    zipcelx({
      filename: `Individual_Donor_Report_${selectedDonor?.label || ''}`,
      sheet: { data: [headers, ...dataRows, summationRow] }
    });
  }

  return {
    currentData, setCurrentData, open, setOpen, updateMyData, skipPageReset,
    columns, data, filteredData, setData, current, edit, setEdit,
    dummy_data, dummy_line_data, options,
    selectedDonor, setSelectedDonor, isLoading, selectedDonorDetails,
    totalAmount, getExcel,
    dateFrom, setDateFrom, dateTo, setDateTo,
  }
}

export default useDonorReport;
